package com.oasisfeng.invaluable.meaning

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.data.Stream
import com.oasisfeng.invaluable.data.Stream.BitStream
import com.oasisfeng.invaluable.data.Stream.OctetStream
import kotlinx.io.bytestring.ByteString
import kotlinx.io.bytestring.decodeToString

/** UTF-8 codec lens between host text and data-layer octet streams. */
object Utf8 {

    /** Encode boundary text as canonical radix-2 octet stream topology. */
    fun encode(text: String): Stream.FixedWidthGrouping = OctetStream.encode(text.encodeToByteArray())

    /**
     * Encode boundary text as byte-granular radix-256 octet stream topology (one link per byte).
     *
     * Renders identically through [render] (the lens self-describes its radix), but costs ~8× fewer links than
     * [encode] — preferred for sizeable content bodies. Schema/protocol names keep [encode] so their link identity
     * is unchanged.
     */
    fun encodeDirect(text: String): Stream.FixedWidthGrouping = OctetStream.encodeDirect(text.encodeToByteArray())

    /** Try to render a Link-backed octet lens as UTF-8 text. */
    fun render(link: NestedLink): String? = OctetStream.render(link)?.let(::render)

    /** Render octets as UTF-8 text. */
    fun render(octets: ByteString): String = octets.decodeToString()

    /** Render a verified stream through the canonical radix-2 octet lens. */
    fun renderCanonical(stream: Stream): String? = OctetStream.renderCanonical(stream)?.let(::render)
}

/** Unicode scalar codec lenses over fixed-width binary frames. */
object UnicodeScalars {

    /** Common scalar-frame alphabets used to choose the fixed binary frame width. */
    object CharacterSet {
        /** Seven-bit scalar frames. */
        const val ASCII = 128

        /** Sixteen-bit scalar frames for the Basic Multilingual Plane. */
        const val UNICODE_BMP = 65536

        /** Twenty-one-bit scalar frames for the full Unicode scalar range. */
        const val UNICODE_FULL = 1_114_112
    }

    /** Encode text as compact fixed-width binary scalar frames. */
    fun encode(content: String, charSetSize: Int = CharacterSet.UNICODE_BMP): Stream.FixedWidthGrouping {
        require(charSetSize >= 2) { "Character set size must be at least 2." }
        require(charSetSize <= CharacterSet.UNICODE_FULL) { "Character set size exceeds Unicode scalar space." }
        val width = characterBitWidth(charSetSize)
        val codePoints = unicodeScalars(content)
        for (codePoint in codePoints) {
            require(codePoint < charSetSize) { "Character U+${codePoint.toString(16)} exceeds charset radix $charSetSize." }
        }

        val bits = mutableListOf<Boolean>()
        for (codePoint in codePoints) appendFixedWidthBinary(codePoint, width, bits)
        return Stream.FixedWidthGrouping(BitStream.encode(bits), Stream.GroupWidth(width))
    }

    /** Render a compact fixed-width binary scalar frame emitted by [encode]. */
    fun render(link: NestedLink): String? {
        val grouping = Stream.FixedWidthGrouping.render(link) ?: return null
        return render(grouping)
    }

    /** Render a compact fixed-width binary scalar frame emitted by [encode]. */
    fun render(grouping: Stream.FixedWidthGrouping): String? {
        val width = grouping.width.value
        if (width > UNICODE_SCALAR_BIT_WIDTH) return null

        val bits = BitStream.render(grouping.stream) ?: return null
        if (bits.size % width != 0) return null

        val codePoints = mutableListOf<Int>()
        var index = 0
        while (index < bits.size) {
            var codePoint = 0L
            repeat(width) {
                codePoint = (codePoint shl 1) or (if (bits[index++]) 1L else 0L)
                if (codePoint > MAX_UNICODE_SCALAR) return null
            }
            codePoints += codePoint.toInt()
        }
        return renderUnicodeScalarString(codePoints)
    }

    /** Render a verified stream as Unicode scalar frames using [charSetSize] to choose the fixed-width lens. */
    fun render(stream: Stream, charSetSize: Int = CharacterSet.UNICODE_BMP): String? {
        require(charSetSize >= 2) { "Character set size must be at least 2." }
        require(charSetSize <= CharacterSet.UNICODE_FULL) { "Character set size exceeds Unicode scalar space." }
        return render(Stream.FixedWidthGrouping(stream, Stream.GroupWidth(characterBitWidth(charSetSize))))
    }

    private fun renderUnicodeScalarString(codePoints: Iterable<Int>): String? =
        buildString {
            for (codePoint in codePoints) {
                if (codePoint !in 0..MAX_UNICODE_SCALAR || codePoint in SURROGATE_RANGE) return null
                appendUnicodeScalar(codePoint)
            }
        }

    private fun characterBitWidth(charSetSize: Int): Int {
        var capacity = 2L
        var width = 1
        while (capacity < charSetSize) {
            capacity = capacity shl 1
            width++
        }
        return width
    }

    private fun appendFixedWidthBinary(value: Int, width: Int, bits: MutableList<Boolean>) {
        require(width in 1..Int.SIZE_BITS) { "Fixed-width scalar frame must fit Int bits." }
        for (offset in width - 1 downTo 0) {
            bits += ((value ushr offset) and 1) != 0
        }
    }

    private fun unicodeScalars(value: String): List<Int> {
        val scalars = mutableListOf<Int>()
        var index = 0
        while (index < value.length) {
            val first = value[index].code
            when {
                first in HIGH_SURROGATE_RANGE -> {
                    require(index + 1 < value.length) { "High surrogate must be followed by a low surrogate." }
                    val second = value[index + 1].code
                    require(second in LOW_SURROGATE_RANGE) { "High surrogate must be followed by a low surrogate." }
                    scalars += 0x10000 + ((first - HIGH_SURROGATE_START) shl 10) + (second - LOW_SURROGATE_START)
                    index += 2
                }
                first in LOW_SURROGATE_RANGE -> throw IllegalArgumentException("Low surrogate must follow a high surrogate.")
                else -> {
                    scalars += first
                    index++
                }
            }
        }
        return scalars
    }

    private fun StringBuilder.appendUnicodeScalar(value: Int) {
        if (value <= 0xffff) {
            append(value.toChar())
        } else {
            val code = value - 0x10000
            append((HIGH_SURROGATE_START + (code ushr 10)).toChar())
            append((LOW_SURROGATE_START + (code and 0x3ff)).toChar())
        }
    }

    private const val HIGH_SURROGATE_START = 0xd800
    private const val LOW_SURROGATE_START = 0xdc00
    private val HIGH_SURROGATE_RANGE = HIGH_SURROGATE_START..0xdbff
    private val LOW_SURROGATE_RANGE = LOW_SURROGATE_START..0xdfff
    private val SURROGATE_RANGE = HIGH_SURROGATE_START..0xdfff
    private const val MAX_UNICODE_SCALAR = 0x10ffff
    private const val UNICODE_SCALAR_BIT_WIDTH = 21
}
